import { useState } from 'react'

import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import {
  AllEmployeeGroupsDocument,
  DeleteEmployeeGroupDocument,
  EmployeeGroupRemoveDataDocument,
  EmployeeGroupRootPageDocument,
  SettingsEmployeeGroupFragment,
  UpdateEmployeeEmployeeGroupDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Select from 'modules/core/components/Inputs/Select'
import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import LegalEntitySelectForm from 'modules/legal_entities/lib/forms/LegalEntitySelectForm'

interface Props {
  employeeGroup: SettingsEmployeeGroupFragment
  onClose: () => void
}

const RemoveModal = ({ employeeGroup, onClose }: Props) => {
  const form = useLazyRef(() => new LegalEntitySelectForm({})).current
  const groupLegalEntityId = employeeGroup.legalEntityId
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const [loadingDelete, setLoadingDelete] = useState<boolean>(false)

  const [DeleteEmployeeGroup] = useMutation(DeleteEmployeeGroupDocument, {
    extractorFn: (data) => data.employeesMutations.deleteEmployeesEmployeeGroup,
  })
  const [UpdateEmployeeEmployeeGroup] = useMutation(UpdateEmployeeEmployeeGroupDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
  })
  const { data, loading } = useQuery(EmployeeGroupRemoveDataDocument, {
    variables: {
      companyIds: [companyId],
    },
  })
  const legalEntities = data?.companies.legalEntitiesConnection.nodes ?? []
  const employees = employeeGroup.employees

  const options = legalEntities
    .filter((legalEntity) => legalEntity.employeeGroups.length > 0)
    .map((legalEntity) => ({
      label: legalEntity.legalName,
      value: legalEntity.id,
    }))

  const newLegalEntityId = String(form.get('legal_entity_id').value)

  const employeeGroupOptions = () => {
    const newLegalEntity = legalEntities.find(({ id }) => id === newLegalEntityId)

    if (!newLegalEntity) return []
    const legalEntityEmployeeGroups = newLegalEntity.employeeGroups ?? []

    return legalEntityEmployeeGroups
      .filter((group) => employeeGroup.id !== group.id)
      .map((group) => ({
        label: group.name,
        value: group.id,
      }))
  }

  const newEmployeeGroupId = String(form.get('employee_group_id').value)

  const disabled = useUnsafeMemo(() => {
    if (!groupLegalEntityId) return true

    if (employeeGroup.main) return true

    const filled = !!newLegalEntityId && !!newEmployeeGroupId

    return !!employees.length && !filled
  }, [groupLegalEntityId, newLegalEntityId, newEmployeeGroupId, employeeGroup, employees.length])

  const updateEmployeeEmployeeGroup = (
    employeeId: string,
    employeeGroupId: string,
    legalEntityId: string
  ) => {
    return UpdateEmployeeEmployeeGroup({
      variables: {
        id: employeeId,
        employeeGroupId: employeeGroupId,
        legalEntityId: legalEntityId,
      },
    })
  }

  const onSubmit = async () => {
    setLoadingDelete(true)

    if (employees.length) {
      const promises = employees.map((employee) =>
        updateEmployeeEmployeeGroup(
          employee.id,
          String(form.get('employee_group_id').value),
          String(form.get('legal_entity_id').value)
        )
      )

      await Promise.all(promises)
    }

    const result = await DeleteEmployeeGroup({
      variables: {
        id: employeeGroup.id,
      },
      refetchQueries: [
        {
          query: AllEmployeeGroupsDocument,
          variables: {
            legalEntityIds: [groupLegalEntityId],
          },
        },
        {
          query: EmployeeGroupRootPageDocument,
          variables: {
            legalEntityIds: [groupLegalEntityId],
          },
        },
      ],
    })

    const handler = handleResult(result, () => null)
    setLoadingDelete(false)

    if (handler.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onClose()
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }
  }

  if (!groupLegalEntityId) return null

  const hasEmployees = !!employees.length

  const withEmployeeDescription = i18n.t(
    'settings.legal_entities.legal_entity.employee_group.remove.with_employees'
  )

  const withoutEmployeeDescription = i18n.t(
    'settings.legal_entities.legal_entity.employee_group.remove.description'
  )

  const description = hasEmployees ? withEmployeeDescription : withoutEmployeeDescription

  return (
    <Modal
      illustration="alert"
      title={i18n.t('settings.legal_entities.legal_entity.employee_group.remove.title')}
      description={loading ? '' : description}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={loading || disabled || loadingDelete} type="negative" submit>
          {i18n.t('settings.legal_entities.legal_entity.employee_group.remove.submit')}
        </Button>
      }
    >
      {loading && (
        <Box alignItems="center">
          <Spinner />
        </Box>
      )}
      {!loading && hasEmployees && (
        <>
          <FormRow>
            <Select
              none={i18n.t('common.none')}
              field={form.get('legal_entity_id')}
              label={i18n.t('common.legal_entity')}
              options={options}
            />
          </FormRow>
          {newLegalEntityId !== '' && (
            <FormRow>
              <Select
                none={i18n.t('common.none')}
                field={form.get('employee_group_id')}
                label={i18n.t('common.employee_group')}
                options={employeeGroupOptions()}
              />
            </FormRow>
          )}
        </>
      )}
    </Modal>
  )
}

export default observer(RemoveModal)
